"use strict";
import { BigNumber } from "@ethersproject/bignumber";
import { Contract } from "@ethersproject/contracts";
import { formatUnits, parseUnits } from "@ethersproject/units";
import { useAccountDrawer } from "components/AccountDrawer/MiniPortfolio/hooks";
import SwapCurrencyInputPanel from "components/CurrencyInputPanel/SwapCurrencyInputPanel";
import { PageWrapper, SwapSection } from "components/swap/styled";
import { useAccount } from "hooks/useAccount";
import useSelectChain from "hooks/useSelectChain";
import { ERC20_APPROVAL_ABI, RING_STABLE_MINTER_ABI } from "pages/Mint/IRingStableMinter";
import {
  MINT_CHAIN_ID,
  MINT_CHAIN_NAME,
  MINT_CONFIG,
  MINT_STABLE_CURRENCIES,
  MINT_USDR,
  RING_STABLE_MINTER_ADDRESS
} from "pages/Mint/MintConfig";
import { getMintOutputAmount, isUsdrMintPrecisionSafe } from "pages/Mint/MintLimit";
import { formatMintError, withWalletRequestTimeout } from "pages/Mint/MintWalletRequest";
import { isMintStableCurrency } from "pages/Mint/MintWhitelistEnforcer";
import { buildGasLimitOverride } from "pages/usdrGas";
import { getUsdrPendingActionLabel } from "pages/usdrTransactionStatus";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MultichainContextProvider } from "state/multichain/MultichainContext";
import { getSigner } from "state/sagas/transactions/utils";
import { useTransactionAdder } from "state/transactions/hooks";
import { TransactionType } from "state/transactions/types";
import { Button, Flex, Text, styled, useIsShortMobileDevice, useMedia } from "ui/src";
import { ArrowUpRight } from "ui/src/components/icons/ArrowUpRight";
import { ExchangeHorizontal } from "ui/src/components/icons/ExchangeHorizontal";
import { Sparkle } from "ui/src/components/icons/Sparkle";
import { isTestnetChain } from "uniswap/src/features/chains/utils";
import { useOnChainCurrencyBalance } from "uniswap/src/features/portfolio/api";
import { setIsTestnetModeEnabled } from "uniswap/src/features/settings/slice";
import { SwapArrowButton } from "uniswap/src/features/transactions/swap/form/body/SwapArrowButton";
import { CurrencyField } from "uniswap/src/types/currency";
import { currencyId } from "uniswap/src/utils/currencyId";
const MintShell = styled(Flex, {
  gap: "$spacing16",
  width: "100%"
});
const PageIntro = styled(Flex, {
  gap: "$spacing4",
  px: "$spacing8",
  width: "100%"
});
const StableSelector = styled(Flex, {
  backgroundColor: "$surface2",
  borderRadius: "$rounded16",
  gap: "$spacing4",
  p: "$spacing4",
  width: "100%"
});
const InfoPanel = styled(Flex, {
  backgroundColor: "$surface1",
  borderColor: "$surface3",
  borderRadius: "$rounded16",
  borderStyle: "solid",
  borderWidth: "$spacing1",
  gap: "$spacing8",
  p: "$spacing12",
  width: "100%"
});
const InputSurface = styled(SwapSection, {
  borderColor: "$surface3",
  height: "auto",
  overflow: "hidden",
  width: "100%",
  hoverStyle: {
    borderColor: "$surface3Hovered"
  },
  focusWithinStyle: {
    borderColor: "$surface3Hovered"
  }
});
const MintErrorPanel = styled(Flex, {
  backgroundColor: "$statusCritical2",
  borderRadius: "$rounded16",
  p: "$spacing12",
  width: "100%"
});
const AfterMintActionList = styled(Flex, {
  gap: "$spacing8",
  width: "100%"
});
const ActionLink = styled(Flex, {
  backgroundColor: "$surface2",
  borderRadius: "$rounded12",
  cursor: "pointer",
  gap: "$spacing12",
  hoverStyle: {
    backgroundColor: "$surface3Hovered",
    y: -1
  },
  minHeight: 64,
  p: "$spacing12",
  width: "100%",
  "$platform-web": {
    transition: "background-color 160ms ease, transform 160ms ease"
  }
});
function getQuoteAmountOut(result) {
  const tuple = result;
  return BigNumber.from(tuple.amountOut ?? tuple[1]);
}
function ConfigDisabledState() {
  const { t } = useTranslation();
  return <PageWrapper maxWidth={480} width="calc(100% - 32px)" px="$none"><MintShell><PageIntro><Text variant="heading3">{t("mint.title")}</Text><Text variant="body3" color="$neutral2">{t("mint.subtitle")}</Text></PageIntro><InfoPanel><Text variant="body2">{t("mint.notConfigured.title")}</Text><Text variant="body3" color="$neutral2">{t("mint.notConfigured.description")}</Text></InfoPanel></MintShell></PageWrapper>;
}
function MintActionLink({
  cta,
  description,
  icon,
  isCompact,
  onPress,
  title
}) {
  if (isCompact) {
    return <ActionLink gap="$spacing8" onPress={onPress}><Flex row alignItems="center" justifyContent="space-between" gap="$spacing8"><Flex row alignItems="center" gap="$spacing10" flex={1} minWidth={0}><Flex
      alignItems="center"
      justifyContent="center"
      width={32}
      height={32}
      borderRadius="$rounded8"
      backgroundColor="$surface3"
      flexShrink={0}
    >{icon}</Flex><Text variant="body2" flex={1} minWidth={0} $platform-web={{ overflowWrap: "anywhere" }}>{title}</Text></Flex><Flex row alignItems="center" gap="$spacing4" flexShrink={0}><Text variant="buttonLabel4" color="$accent1">{cta}</Text><ArrowUpRight size="$icon.16" color="$accent1" /></Flex></Flex><Text variant="body4" color="$neutral2" $platform-web={{ overflowWrap: "anywhere" }}>{description}</Text></ActionLink>;
  }
  return <ActionLink row alignItems="center" justifyContent="space-between" onPress={onPress}><Flex row alignItems="center" gap="$spacing12" flex={1} minWidth={0}><Flex
    alignItems="center"
    justifyContent="center"
    width={36}
    height={36}
    borderRadius="$rounded12"
    backgroundColor="$surface3"
    flexShrink={0}
  >{icon}</Flex><Flex gap="$spacing2" flex={1} minWidth={0}><Text variant="body2" $platform-web={{ overflowWrap: "anywhere" }}>{title}</Text><Text variant="body4" color="$neutral2" $platform-web={{ overflowWrap: "anywhere" }}>{description}</Text></Flex></Flex><Flex row alignItems="center" gap="$spacing4" flexShrink={0}><Text variant="buttonLabel4" color="$accent1">{cta}</Text><ArrowUpRight size="$icon.16" color="$accent1" /></Flex></ActionLink>;
}
function MintForm({
  chainId,
  minterAddress,
  stables,
  usdr
}) {
  const account = useAccount();
  const accountDrawer = useAccountDrawer();
  const addTransaction = useTransactionAdder();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const media = useMedia();
  const selectChain = useSelectChain({ allowChainOutsideMode: true });
  const isShortMobileDevice = useIsShortMobileDevice();
  const [selectedStable, setSelectedStable] = useState(stables[0]);
  const [inputValue, setInputValue] = useState("");
  const [submissionPhase, setSubmissionPhase] = useState("idle");
  const [error, setError] = useState();
  const { balance: selectedStableBalance } = useOnChainCurrencyBalance(selectedStable, account.address);
  const { balance: usdrBalance } = useOnChainCurrencyBalance(usdr, account.address);
  const isSubmitting = submissionPhase !== "idle";
  const currencyBalances = useMemo(
    () => ({
      [CurrencyField.INPUT]: selectedStableBalance,
      [CurrencyField.OUTPUT]: usdrBalance
    }),
    [selectedStableBalance, usdrBalance]
  );
  useEffect(() => {
    setSubmissionPhase("idle");
    setError(void 0);
  }, [account.address, account.chainId, account.connector?.id, account.status]);
  const parsedInputAmount = useMemo(() => {
    try {
      const parsed = parseUnits(inputValue || "0", selectedStable.decimals);
      return parsed.gt(0) ? parsed : void 0;
    } catch {
      return void 0;
    }
  }, [inputValue, selectedStable.decimals]);
  const isPrecisionSafe = useMemo(() => {
    if (!parsedInputAmount) {
      return true;
    }
    return isUsdrMintPrecisionSafe(BigInt(parsedInputAmount.toString()), selectedStable.decimals, usdr.decimals);
  }, [parsedInputAmount, selectedStable.decimals, usdr.decimals]);
  const outputValue = useMemo(() => {
    if (!parsedInputAmount || !isPrecisionSafe) {
      return "";
    }
    return formatUnits(getMintOutputAmount(parsedInputAmount, selectedStable.decimals, usdr.decimals), usdr.decimals);
  }, [isPrecisionSafe, parsedInputAmount, selectedStable.decimals, usdr.decimals]);
  const maxInputAmount = useMemo(
    () => selectedStableBalance ? BigNumber.from(selectedStableBalance.quotient.toString()) : void 0,
    [selectedStableBalance]
  );
  const isWrongChain = account.status === "connected" && account.chainId !== chainId;
  const canSubmit = Boolean(account.status === "connected" && parsedInputAmount && isPrecisionSafe && !isSubmitting);
  const isSubmitDisabled = isSubmitting || account.status === "connected" && !isWrongChain && !canSubmit;
  const buttonSize = isShortMobileDevice ? "small" : "large";
  const buttonVariant = isSubmitDisabled ? "default" : "branded";
  const buttonEmphasis = account.status === "connected" && !isSubmitDisabled ? "primary" : "secondary";
  const defaultSwapStable = useMemo(
    () => stables.find((stable) => stable.symbol === "USDC") ?? selectedStable,
    [selectedStable, stables]
  );
  const swapUsdrUrl = useMemo(
    () => `/swap?inputCurrency=${usdr.address}&outputCurrency=${defaultSwapStable.address}`,
    [defaultSwapStable.address, usdr.address]
  );
  const buttonText = useMemo(() => {
    const pendingLabel = getUsdrPendingActionLabel(submissionPhase, t);
    if (pendingLabel) {
      return pendingLabel;
    }
    if (account.status !== "connected") {
      return t("common.connectWallet.button");
    }
    if (isWrongChain) {
      return t("mint.button.switchNetwork", { network: MINT_CHAIN_NAME });
    }
    if (!parsedInputAmount) {
      return t("mint.button.enterAmount");
    }
    if (!isPrecisionSafe) {
      return t("mint.button.amountTooSmall");
    }
    return t("mint.button.mintUsdr");
  }, [account.status, isPrecisionSafe, isWrongChain, parsedInputAmount, submissionPhase, t]);
  const handleMax = useCallback(() => {
    if (maxInputAmount) {
      setInputValue(formatUnits(maxInputAmount, selectedStable.decimals));
    }
  }, [maxInputAmount, selectedStable.decimals]);
  const handleStableSelect = useCallback(
    (stable) => {
      if (!isMintStableCurrency(stable, MINT_CONFIG)) {
        return;
      }
      setSelectedStable(stable);
      setError(void 0);
    },
    [setSelectedStable]
  );
  const handleSubmit = useCallback(async () => {
    setError(void 0);
    if (account.status !== "connected") {
      accountDrawer.open();
      return;
    }
    if (account.chainId !== chainId) {
      setSubmissionPhase("switching-network");
      try {
        dispatch(setIsTestnetModeEnabled(isTestnetChain(chainId)));
        const didSwitch = await withWalletRequestTimeout(selectChain(chainId));
        if (!didSwitch) {
          setError(t("mint.error.switchNetwork", { network: MINT_CHAIN_NAME }));
        }
      } finally {
        setSubmissionPhase("idle");
      }
      return;
    }
    if (!parsedInputAmount || !isPrecisionSafe) {
      return;
    }
    setSubmissionPhase("preparing");
    try {
      const signer = await getSigner(account.address);
      const stableContract = new Contract(selectedStable.address, ERC20_APPROVAL_ABI, signer);
      const minterContract = new Contract(minterAddress, RING_STABLE_MINTER_ABI, signer);
      const quote = await minterContract.quoteMint(selectedStable.address, parsedInputAmount);
      const amountOut = getQuoteAmountOut(quote);
      const allowance = await stableContract.allowance(account.address, minterAddress);
      if (allowance.lt(parsedInputAmount)) {
        if (!allowance.isZero()) {
          setSubmissionPhase("approving");
          const resetApprovalOverrides = await buildGasLimitOverride(
            stableContract.estimateGas.approve(minterAddress, 0)
          );
          const resetApprovalTx = await stableContract.approve(minterAddress, 0, resetApprovalOverrides);
          addTransaction(resetApprovalTx, {
            type: TransactionType.APPROVAL,
            tokenAddress: selectedStable.address,
            spender: minterAddress,
            amount: "0"
          });
          await resetApprovalTx.wait(1);
        }
        setSubmissionPhase("approving");
        const approveOverrides = await buildGasLimitOverride(
          stableContract.estimateGas.approve(minterAddress, parsedInputAmount)
        );
        const approveTx = await stableContract.approve(minterAddress, parsedInputAmount, approveOverrides);
        addTransaction(approveTx, {
          type: TransactionType.APPROVAL,
          tokenAddress: selectedStable.address,
          spender: minterAddress,
          amount: parsedInputAmount.toString()
        });
        await approveTx.wait(1);
      }
      setSubmissionPhase("minting");
      const mintOverrides = await buildGasLimitOverride(
        minterContract.estimateGas.mint(selectedStable.address, parsedInputAmount, amountOut, account.address)
      );
      const mintTx = await minterContract.mint(
        selectedStable.address,
        parsedInputAmount,
        amountOut,
        account.address,
        mintOverrides
      );
      addTransaction(mintTx, {
        type: TransactionType.MINT,
        inputCurrencyId: currencyId(selectedStable),
        inputCurrencyAmountRaw: parsedInputAmount.toString(),
        inputCurrencySymbol: selectedStable.symbol,
        inputCurrencyDecimals: selectedStable.decimals,
        inputCurrencyName: selectedStable.name,
        outputCurrencyId: currencyId(usdr),
        outputCurrencyAmountRaw: amountOut.toString(),
        outputCurrencySymbol: usdr.symbol,
        outputCurrencyDecimals: usdr.decimals,
        outputCurrencyName: usdr.name
      });
      await mintTx.wait(1);
      setInputValue("");
    } catch (submitError) {
      setError(formatMintError(submitError, (key, options) => t(key, options)));
    } finally {
      setSubmissionPhase("idle");
    }
  }, [
    account,
    accountDrawer,
    addTransaction,
    chainId,
    dispatch,
    isPrecisionSafe,
    minterAddress,
    parsedInputAmount,
    selectChain,
    selectedStable,
    t,
    usdr
  ]);
  return <MultichainContextProvider initialChainId={chainId}><PageWrapper maxWidth={480} width="calc(100% - 32px)" px="$none" pb="$spacing48"><MintShell><PageIntro><Text variant="heading3">{t("mint.title")}</Text><Text variant="body3" color="$neutral2">{t("mint.subtitle")}</Text></PageIntro><StableSelector row>{stables.map((stable) => {
    const isSelected = stable.equals(selectedStable);
    return <Button
      key={stable.address}
      fill
      size="small"
      emphasis={isSelected ? "primary" : "secondary"}
      onPress={() => handleStableSelect(stable)}
    >{stable.symbol}</Button>;
  })}</StableSelector><Flex gap="$spacing4"><InputSurface><SwapCurrencyInputPanel
    value={inputValue}
    onUserInput={(value) => {
      setInputValue(value);
      setError(void 0);
    }}
    onMax={handleMax}
    showMaxButton
    label={t("mint.input.deposit")}
    currency={selectedStable}
    otherCurrency={usdr}
    currencyField={CurrencyField.INPUT}
    currencyBalance={currencyBalances[CurrencyField.INPUT]}
    id="usdr-mint-input"
  /></InputSurface><Flex alignItems="center" height={0} justifyContent="center" zIndex="$mask"><Flex position="absolute"><SwapArrowButton disabled /></Flex></Flex><InputSurface><SwapCurrencyInputPanel
    value={outputValue}
    onUserInput={() => void 0}
    showMaxButton={false}
    label={t("mint.input.receive")}
    currency={usdr}
    otherCurrency={selectedStable}
    currencyField={CurrencyField.OUTPUT}
    currencyBalance={currencyBalances[CurrencyField.OUTPUT]}
    id="usdr-mint-output"
    disabled
  /></InputSurface></Flex><InfoPanel><Text variant="subheading2">{t("mint.afterMint.title")}</Text><Text variant="body4" color="$neutral2">{t("mint.afterMint.description")}</Text></InfoPanel><AfterMintActionList><MintActionLink
    title={t("mint.link.swap.title")}
    description={t("mint.link.swap.description")}
    cta={t("mint.link.swap.cta")}
    icon={<ExchangeHorizontal size="$icon.20" color="$neutral1" />}
    isCompact={media.sm}
    onPress={() => navigate(swapUsdrUrl)}
  /><MintActionLink
    title={t("mint.link.earn.title")}
    description={t("mint.link.earn.description")}
    cta={t("mint.link.earn.cta")}
    icon={<Sparkle size="$icon.20" color="$neutral1" />}
    isCompact={media.sm}
    onPress={() => navigate("/earn")}
  /></AfterMintActionList><Flex alignItems="center" gap={isShortMobileDevice ? "$spacing8" : "$spacing16"}><Flex row alignSelf="stretch"><Button
    fill
    size={buttonSize}
    variant={buttonVariant}
    emphasis={buttonEmphasis}
    loading={isSubmitting}
    isDisabled={isSubmitDisabled}
    animation="simple"
    onPress={handleSubmit}
  >{buttonText}</Button></Flex></Flex>{error && <MintErrorPanel data-testid="usdr-mint-error"><Text variant="body3" color="$statusCritical" $platform-web={{ wordBreak: "break-word" }}>{error}</Text></MintErrorPanel>}</MintShell></PageWrapper></MultichainContextProvider>;
}
export default function MintPage() {
  if (MINT_CONFIG.error || !MINT_CHAIN_ID || !RING_STABLE_MINTER_ADDRESS || !MINT_USDR || MINT_STABLE_CURRENCIES.length !== 3) {
    return <ConfigDisabledState />;
  }
  return <MintForm
    chainId={MINT_CHAIN_ID}
    minterAddress={RING_STABLE_MINTER_ADDRESS}
    stables={MINT_STABLE_CURRENCIES}
    usdr={MINT_USDR}
  />;
}
